import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useUser } from '../contexts/UserContext';
import { TextField, Button, Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem, ListItemText, IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';

const UserManagerComponent = () => {
    const { user } = useUser();
    const [users, setUsers] = useState([]);
    const [currentUser, setCurrentUser] = useState({ id: null, name: '', role: '', email: '', username: '', password: '' });
    const [open, setOpen] = useState(false);
    const [error, setError] = useState('');
    const [isEditMode, setIsEditMode] = useState(false);

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_AUTH_URL}/users`, {
                    headers: { Authorization: `Bearer ${user.token}` },
                });
                setUsers(response.data);
            } catch (err) {
                setError(err.response?.data?.message || 'An error occurred while fetching users');
            }
        };

        if (user.token) {
            fetchUsers();
        }
    }, [user]);

    const handleOpen = (user) => {
        setIsEditMode(!!user);
        setCurrentUser(user || { id: null, name: '', role: '', email: '', username: '', password: '' });
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setCurrentUser({ id: null, name: '', role: '', email: '', username: '', password: '' });
        setError('');
    };

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        setCurrentUser({ ...currentUser, [name]: value });
    };

    const handleAddOrUpdateUser = async () => {
        try {
            if (isEditMode) {
                const response = await axios.put(`${process.env.REACT_APP_AUTH_URL}/users/${currentUser.id}`, {
                    name: currentUser.name,
                    role: currentUser.role,
                    email: currentUser.email,
                    username: currentUser.username,
                    password: currentUser.password,
                }, {
                    headers: { Authorization: `Bearer ${user.token}` },
                });
                setUsers(users.map((user) => (user.id === currentUser.id ? response.data : user)));
            } else {
                const response = await axios.post(`${process.env.REACT_APP_AUTH_URL}/adduser`, {
                    name: currentUser.name,
                    role: currentUser.role,
                    email: currentUser.email,
                    username: currentUser.username,
                    password: currentUser.password,
                }, {
                    headers: { Authorization: `Bearer ${user.token}` },
                });
                setUsers([...users, response.data]);
            }
            handleClose();
        } catch (err) {
            setError(err.response?.data?.message || 'An error occurred while saving the user');
        }
    };

    const handleDelete = async (id) => {
        try {
            await axios.delete(`${process.env.REACT_APP_AUTH_URL}/users/${id}`, {
                headers: { Authorization: `Bearer ${user.token}` },
            });
            setUsers(users.filter(user => user.id !== id));
        } catch (err) {
            setError(err.response?.data?.message || 'An error occurred while deleting the user');
        }
    };

    if (!user.token || !user.hasRoles(['jedimaster'])) {
        return <p>Only admins can see/edit users.</p>;
    }

    return (
        <div>
            <Button variant="contained" color="primary" onClick={() => handleOpen(null)}>
                Add User
            </Button>
            {error && typeof error === 'string' && <p>{error}</p>}
            <List>
                {Array.isArray(users) && users.map(managedUser => (
                    <ListItem key={managedUser.id}>
                        <IconButton edge="end" aria-label="edit" onClick={() => handleOpen(managedUser)} sx={{ mr: 1 }}>
                            <EditIcon />
                        </IconButton>
                        <ListItemText primary={managedUser.name} secondary={managedUser.email} />
                        <IconButton edge="end" aria-label="delete" disabled={user.data.id === managedUser.id} onClick={() => handleDelete(managedUser.id)}>
                            <DeleteIcon />
                        </IconButton>
                    </ListItem>
                ))}
            </List>

            <Dialog open={open} onClose={handleClose}>
                <DialogTitle>{isEditMode ? 'Edit User' : 'Add New User'}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        id="name"
                        label="Name"
                        type="text"
                        fullWidth
                        name="name"
                        value={currentUser.name}
                        onChange={handleInputChange}
                    />
                    <TextField
                        margin="dense"
                        id="role"
                        label="Role"
                        type="text"
                        fullWidth
                        name="role"
                        value={currentUser.role}
                        onChange={handleInputChange}
                    />
                    <TextField
                        margin="dense"
                        id="email"
                        label="Email"
                        type="email"
                        fullWidth
                        name="email"
                        value={currentUser.email}
                        onChange={handleInputChange}
                    />
                    <TextField
                        margin="dense"
                        id="username"
                        label="Username"
                        type="text"
                        fullWidth
                        name="username"
                        value={currentUser.username}
                        onChange={handleInputChange}
                    />
                    <TextField
                        margin="dense"
                        id="password"
                        label="Password"
                        type="password"
                        fullWidth
                        name="password"
                        value={currentUser.password}
                        onChange={handleInputChange}
                    />
                    {error && <p>{error}</p>}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button onClick={handleAddOrUpdateUser}>{isEditMode ? 'Update User' : 'Add User'}</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default UserManagerComponent;
